import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";

import { SweetAlertShow } from "../master/Master";
import { fetchBusLocations } from "../../api/Transport";
import { imgBaseUrl } from "../../api/api";

const POLL_INTERVAL_MS = 15000;
const LIVE_THRESHOLD_MIN = 5;

/* =========================================================
   Helpers
========================================================= */

const parseDate = (value) => {
  if (!value) return null;

  return new Date(value.includes("T") ? value : value.replace(" ", "T"));
};

const formatDateTime = (value) => {
  const date = parseDate(value);

  if (!date || Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value) => {
  const date = parseDate(value);

  if (!date || Number.isNaN(date.getTime())) return "—";

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const minutesSince = (value) => {
  const date = parseDate(value);

  if (!date || Number.isNaN(date.getTime())) return null;

  return Math.floor((Date.now() - date.getTime()) / 60000);
};

const formatDuration = (start, end) => {
  const startDate = parseDate(start);
  const endDate = parseDate(end);

  if (!startDate) return "—";

  const finalDate = endDate || new Date();

  const diff = Math.max(
    0,
    Math.floor((finalDate.getTime() - startDate.getTime()) / 60000),
  );

  if (diff < 60) return `${diff} min`;

  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;

  return `${hours}h ${minutes}m`;
};

/* =========================================================
   Main Component
========================================================= */

export default function BusLiveLocation() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const passedBus = state?.bus;

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const polylineRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const locationMarkersRef = useRef([]);
  const pollRef = useRef(null);

  const tripCompletedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [busData, setBusData] = useState(passedBus || null);
  const [locations, setLocations] = useState([]);

  const [autoRefresh, setAutoRefresh] = useState(true);

  /* =========================================================
     Bus Icon
  ========================================================= */

  const busIcon = useMemo(
    () =>
      L.divIcon({
        className: "",
        html: `
          <div style="
            width:42px;
            height:42px;
            border-radius:50%;
            background:#185fa5;
            display:flex;
            align-items:center;
            justify-content:center;
            box-shadow:
              0 0 0 5px rgba(24,95,165,0.18),
              0 5px 14px rgba(0,0,0,0.25);
            color:#fff;
            font-size:19px;
            border:3px solid #fff;
          ">
            <i class="ri-bus-2-fill"></i>
          </div>
        `,
        iconSize: [42, 42],
        iconAnchor: [21, 21],
        popupAnchor: [0, -22],
      }),
    [],
  );

  /* =========================================================
     Load API
  ========================================================= */

  const loadLocations = useCallback(
    async (silent = false) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await fetchBusLocations(id);

        if (response?.data?.status) {
          /*
            Actual API structure:

            response.data.data = {
              id,
              bus_number,
              driver_name,
              ...
              bus_locations: []
            }
          */

          const data = response.data.data;

          setBusData(data || passedBus || null);

          const points = Array.isArray(data?.bus_locations)
            ? [...data.bus_locations]
            : [];

          /*
            Sort oldest -> newest.
            This ensures the last point is the current point.
          */

          points.sort(
            (a, b) =>
              parseDate(a.date_time)?.getTime() -
              parseDate(b.date_time)?.getTime(),
          );

          setLocations(points);
          const latestLocation = points.length
            ? points[points.length - 1]
            : null;

          const latestTrip = latestLocation?.bus_trip || null;

          if (latestTrip?.status === "2") {
            tripCompletedRef.current = true;
            setAutoRefresh(false);
          } else {
            tripCompletedRef.current = false;
          }
        } else if (!silent) {
          SweetAlertShow(
            "info",
            "Warning",
            response?.data?.message || "No location data found.",
          );
        }
      } catch (error) {
        if (!silent) {
          SweetAlertShow(
            "error",
            "Error",
            error?.response?.data?.message ??
              error?.response?.message ??
              error?.message,
          );
        }
      } finally {
        if (silent) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [id, passedBus],
  );

  /* =========================================================
     Initial load + Auto refresh
  ========================================================= */

  useEffect(() => {
    loadLocations();

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [loadLocations]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (!autoRefresh || tripCompletedRef.current) {
      return;
    }

    pollRef.current = setInterval(() => {
      if (!tripCompletedRef.current) {
        loadLocations(true);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [autoRefresh, loadLocations]);

  /* =========================================================
     Current Location
  ========================================================= */

  const current = locations.length ? locations[locations.length - 1] : null;

  const firstLocation = locations.length ? locations[0] : null;

  const currentMinutesAgo = current ? minutesSince(current.date_time) : null;

  const isLive =
    currentMinutesAgo !== null && currentMinutesAgo <= LIVE_THRESHOLD_MIN;

  /* =========================================================
     Current Trip
  ========================================================= */

  const currentTrip = current?.bus_trip || null;

  /* =========================================================
     Map Initialization
  ========================================================= */

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    mapInstance.current = L.map(mapRef.current, {
      zoomControl: false,
      attributionControl: true,
    }).setView([26.9124, 75.7873], 13);

    L.control
      .zoom({
        position: "bottomright",
      })
      .addTo(mapInstance.current);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(mapInstance.current);

    return () => {
      mapInstance.current?.remove();
      mapInstance.current = null;
    };
  }, []);

  /* =========================================================
     Update Map
  ========================================================= */

  useEffect(() => {
    if (!mapInstance.current || !locations.length) return;

    const validLocations = locations.filter((point) => {
      const lat = parseFloat(point.latitude);
      const lng = parseFloat(point.longitude);

      return Number.isFinite(lat) && Number.isFinite(lng);
    });

    if (!validLocations.length) return;

    const pathPoints = validLocations.map((point) => [
      parseFloat(point.latitude),
      parseFloat(point.longitude),
    ]);

    const currentLocation = validLocations[validLocations.length - 1];

    const currentPoint = [
      parseFloat(currentLocation.latitude),
      parseFloat(currentLocation.longitude),
    ];

    /* =========================================================
     ROUTE LINE
  ========================================================= */

    if (!polylineRef.current) {
      polylineRef.current = L.polyline(pathPoints, {
        color: "#185fa5",
        weight: 5,
        opacity: 0.8,
        lineCap: "round",
        lineJoin: "round",
      }).addTo(mapInstance.current);
    } else {
      polylineRef.current.setLatLngs(pathPoints);
    }

    /* =========================================================
     REMOVE OLD LOCATION POINT MARKERS
  ========================================================= */

    locationMarkersRef.current.forEach((marker) => {
      marker.remove();
    });

    locationMarkersRef.current = [];

    /* =========================================================
     LOCATION POINTS
  ========================================================= */

    validLocations.forEach((location, index) => {
      const lat = parseFloat(location.latitude);
      const lng = parseFloat(location.longitude);

      const isFirst = index === 0;
      const isLast = index === validLocations.length - 1;

      /*
      Current location is already represented by the
      bus marker, so don't create another numbered marker
      on top of it.
    */

      if (isLast) return;

      const pointIcon = L.divIcon({
        className: "bus-location-point",
        html: `
        <div class="location-point ${
          isFirst ? "location-start" : "location-history"
        }">
          ${index + 1}
        </div>
      `,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });

      const pointMarker = L.marker([lat, lng], {
        icon: pointIcon,
        zIndexOffset: 500,
      }).addTo(mapInstance.current);

      pointMarker.bindTooltip(
        `
        <div style="font-size:12px">
          <strong>Location ${index + 1}</strong><br/>
          ${formatTime(location.date_time)}
        </div>
      `,
        {
          direction: "top",
          offset: [0, -12],
        },
      );

      pointMarker.bindPopup(`
      <div style="min-width:190px">
        <div style="
          font-weight:700;
          font-size:14px;
          margin-bottom:8px;
        ">
          Location ${index + 1}
        </div>

        <div style="font-size:12px;margin-bottom:5px">
          <strong>Time:</strong>
          ${formatDateTime(location.date_time)}
        </div>

        <div style="font-size:12px;margin-bottom:5px">
          <strong>Latitude:</strong>
          ${lat.toFixed(6)}
        </div>

        <div style="font-size:12px">
          <strong>Longitude:</strong>
          ${lng.toFixed(6)}
        </div>
      </div>
    `);

      locationMarkersRef.current.push(pointMarker);
    });

    /* =========================================================
     CURRENT BUS MARKER
  ========================================================= */

    if (!markerRef.current) {
      markerRef.current = L.marker(currentPoint, {
        icon: busIcon,
        zIndexOffset: 1000,
      }).addTo(mapInstance.current);
    } else {
      markerRef.current.setLatLng(currentPoint);
    }

    markerRef.current.bindPopup(`
    <div style="min-width:210px">

      <div style="
        font-weight:700;
        font-size:15px;
        margin-bottom:8px;
      ">
        <i class="ri-bus-2-fill me-1"></i>
        ${busData?.bus_number || "Bus"}
      </div>

      <div style="font-size:13px;margin-bottom:5px">
        <strong>Driver:</strong>
        ${busData?.driver_name || "—"}
      </div>

      <div style="font-size:13px;margin-bottom:5px">
        <strong>Location:</strong>
        ${validLocations.length}
      </div>

      <div style="font-size:13px;margin-bottom:5px">
        <strong>Latitude:</strong>
        ${currentPoint[0].toFixed(6)}
      </div>

      <div style="font-size:13px;margin-bottom:5px">
        <strong>Longitude:</strong>
        ${currentPoint[1].toFixed(6)}
      </div>

      <div style="font-size:13px">
        <strong>Updated:</strong>
        ${formatDateTime(currentLocation.date_time)}
      </div>

    </div>
  `);

    /* =========================================================
     CURRENT LOCATION HIGHLIGHT
  ========================================================= */

    if (!endMarkerRef.current) {
      endMarkerRef.current = L.circleMarker(currentPoint, {
        radius: 20,
        color: "#185fa5",
        weight: 2,
        fillColor: "#185fa5",
        fillOpacity: 0.1,
        interactive: false,
        className: "current-location-pulse",
      }).addTo(mapInstance.current);
    } else {
      endMarkerRef.current.setLatLng(currentPoint);
    }

    /* =========================================================
     AUTOMATICALLY SHOW ALL LOCATIONS
  ========================================================= */

    if (pathPoints.length === 1) {
      mapInstance.current.setView(currentPoint, 16);
    } else {
      const bounds = L.latLngBounds(pathPoints);

      mapInstance.current.fitBounds(bounds, {
        padding: [80, 80],
        maxZoom: 16,
        animate: true,
      });
    }
  }, [locations, busData, busIcon]);

  /* =========================================================
     Map Controls
  ========================================================= */

  const recenterMap = () => {
    if (!mapInstance.current || !current) return;

    const position = [
      parseFloat(current.latitude),
      parseFloat(current.longitude),
    ];

    mapInstance.current.setView(position, 16, {
      animate: true,
    });

    markerRef.current?.openPopup();
  };

  const fitRoute = () => {
    if (!mapInstance.current || !locations.length) return;

    const validPoints = locations
      .filter((point) => {
        const lat = parseFloat(point.latitude);
        const lng = parseFloat(point.longitude);

        return Number.isFinite(lat) && Number.isFinite(lng);
      })
      .map((point) => [
        parseFloat(point.latitude),
        parseFloat(point.longitude),
      ]);

    if (!validPoints.length) return;

    if (validPoints.length === 1) {
      mapInstance.current.setView(validPoints[0], 16, {
        animate: true,
      });

      return;
    }

    mapInstance.current.fitBounds(validPoints, {
      padding: [80, 80],
      maxZoom: 16,
      animate: true,
    });
  };

  /* =========================================================
     Derived Information
  ========================================================= */

  const coordinates = current
    ? `${parseFloat(current.latitude).toFixed(6)}, ${parseFloat(
        current.longitude,
      ).toFixed(6)}`
    : "No location";

  const trackingDuration = firstLocation
    ? formatDuration(
        currentTrip?.start_time
          ? `${currentTrip.date} ${currentTrip.start_time}`
          : firstLocation.date_time,
        currentTrip?.end_time
          ? `${currentTrip.date} ${currentTrip.end_time}`
          : null,
      )
    : "—";

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Header />

        <div className="layout-page">
          <Nav />

          <div className="content-wrapper">
            <div className="container-fluid mt-2">
              <div className="row">
                <Master />

                <div className="col-md-12">
                  {/* =====================================================
                      HEADER
                  ===================================================== */}

                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <button
                            className="btn btn-icon btn-outline-secondary rounded-3"
                            onClick={() => navigate(-1)}
                            title="Back"
                          >
                            <i className="ri-arrow-left-line" />
                          </button>

                          {busData?.bus_image ? (
                            <img
                              src={imgBaseUrl + busData.bus_image}
                              alt={busData.bus_number}
                              style={{
                                width: 52,
                                height: 52,
                                borderRadius: 14,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center bg-label-primary rounded-3"
                              style={{
                                width: 52,
                                height: 52,
                                fontSize: 23,
                              }}
                            >
                              <i className="ri-bus-2-fill" />
                            </div>
                          )}

                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <h5 className="mb-0 fw-bold">
                                {busData?.bus_number ||
                                  passedBus?.bus_number ||
                                  `Bus #${id}`}
                              </h5>

                              <span
                                className={`badge bg-label-${
                                  currentTrip?.status === "2"
                                    ? "success"
                                    : isLive
                                      ? "success"
                                      : "secondary"
                                }`}
                              >
                                <i
                                  className={`ri-${
                                    currentTrip?.status === "2"
                                      ? "checkbox-circle-line"
                                      : isLive
                                        ? "radio-button-line"
                                        : "time-line"
                                  } me-1`}
                                />

                                {currentTrip?.status === "2"
                                  ? "Trip Completed"
                                  : isLive
                                    ? "Live"
                                    : currentMinutesAgo !== null
                                      ? `Updated ${currentMinutesAgo}m ago`
                                      : "No Data"}
                              </span>
                            </div>

                            <div className="text-muted small mt-1">
                              {(busData?.driver_number ||
                                passedBus?.driver_number) && (
                                <a
                                  href={`tel:${
                                    busData?.driver_number ||
                                    passedBus?.driver_number
                                  }`}
                                  className="ms-3 text-decoration-none"
                                >
                                  <i className="ri-phone-fill text-success me-1" />

                                  {busData?.driver_number ||
                                    passedBus?.driver_number}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Header Actions */}

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            className={`btn btn-sm rounded-3 ${
                              autoRefresh
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => {
                              if (tripCompletedRef.current) return;

                              setAutoRefresh((value) => !value);
                            }}
                            disabled={tripCompletedRef.current}
                          >
                            <i className="ri-refresh-line me-1" />

                            {tripCompletedRef.current
                              ? "Trip Completed"
                              : autoRefresh
                                ? "Live Updates"
                                : "Updates Paused"}
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-3"
                            onClick={() => loadLocations(true)}
                            disabled={refreshing}
                          >
                            <i
                              className={`ri-refresh-line me-1 ${
                                refreshing ? "spin" : ""
                              }`}
                            />
                            Refresh
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* =====================================================
                      SUMMARY CARDS
                  ===================================================== */}

                  <div className="row g-3 mb-3">
                    {/* Current Location */}

                    {/* <div className="col-xl-3 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="bg-label-primary rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 44,
                                height: 44,
                              }}
                            >
                              <i className="ri-map-pin-2-line fs-5" />
                            </div>

                            <div>
                              <small className="text-muted">
                                Current Location
                              </small>

                              <div
                                className="fw-semibold text-truncate"
                                style={{
                                  maxWidth: 220,
                                }}
                              >
                                {coordinates}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> */}

                    {/* Capacity */}

                    {/* <div className="col-xl-3 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="bg-label-info rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 44,
                                height: 44,
                              }}
                            >
                              <i className="ri-group-line fs-5" />
                            </div>

                            <div>
                              <small className="text-muted">Bus Capacity</small>

                              <div className="fw-semibold">
                                {busData?.capacity ?? "—"} Seats
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> */}

                    {/* GPS Points */}

                    {/* <div className="col-xl-3 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="bg-label-warning rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 44,
                                height: 44,
                              }}
                            >
                              <i className="ri-route-line fs-5" />
                            </div>

                            <div>
                              <small className="text-muted">GPS Points</small>

                              <div className="fw-semibold">
                                {locations.length}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> */}

                    {/* Last Update */}

                    {/* <div className="col-xl-3 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className={`bg-label-${
                                isLive ? "success" : "secondary"
                              } rounded-3 d-flex align-items-center justify-content-center`}
                              style={{
                                width: 44,
                                height: 44,
                              }}
                            >
                              <i className="ri-time-line fs-5" />
                            </div>

                            <div>
                              <small className="text-muted">Last Update</small>

                              <div className="fw-semibold">
                                {current?.date_time
                                  ? formatTime(current.date_time)
                                  : "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> */}
                  </div>

                  {/* =====================================================
                      TRIP INFORMATION
                  ===================================================== */}

                  {currentTrip && (
                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h6 className="fw-bold mb-1">
                              <i className="ri-route-line text-primary me-2" />
                              Current Trip
                            </h6>

                            <small className="text-muted">
                              Trip #{currentTrip.id}
                            </small>
                          </div>

                          <span
                            className={`badge ${
                              currentTrip.status === "2"
                                ? "bg-label-success"
                                : "bg-label-warning"
                            }`}
                          >
                            {currentTrip.status === "2"
                              ? "Completed"
                              : "Active"}
                          </span>
                        </div>

                        <div className="row g-3">
                          <div className="col-md-3">
                            <small className="text-muted d-block">Route</small>

                            <span className="fw-semibold">
                              {currentTrip.root_name || "Route not specified"}
                            </span>
                          </div>

                          <div className="col-md-3">
                            <small className="text-muted d-block">Date</small>

                            <span className="fw-semibold">
                              {currentTrip.date || "—"}
                            </span>
                          </div>

                          <div className="col-md-3">
                            <small className="text-muted d-block">
                              Start Time
                            </small>

                            <span className="fw-semibold">
                              {currentTrip.start_time || "—"}
                            </span>
                          </div>

                          <div className="col-md-3">
                            <small className="text-muted d-block">
                              End Time
                            </small>

                            <span className="fw-semibold">
                              {currentTrip.end_time || "Running"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* =====================================================
                      MAP
                  ===================================================== */}

                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-header bg-white border-0 py-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <h6 className="fw-bold mb-1">
                            <i className="ri-map-2-line text-primary me-2" />
                            Live Bus Location
                          </h6>

                          <small className="text-muted">
                            {current?.date_time
                              ? `Last location received ${formatDateTime(
                                  current.date_time,
                                )}`
                              : "Waiting for location data"}
                          </small>
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary rounded-3"
                            onClick={fitRoute}
                            disabled={!locations.length}
                            title="Show complete route"
                          >
                            <i className="ri-route-line me-1" />
                            Fit Route
                          </button>

                          <button
                            className="btn btn-sm btn-primary rounded-3"
                            onClick={recenterMap}
                            disabled={!current}
                            title="Center on bus"
                          >
                            <i className="ri-focus-3-line me-1" />
                            Recenter
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="card-body p-0 position-relative">
                      {loading && (
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                          style={{
                            background: "rgba(255,255,255,0.72)",
                            zIndex: 1000,
                          }}
                        >
                          <div className="text-center">
                            <span
                              className="spinner-border text-primary"
                              role="status"
                            />

                            <div className="small text-muted mt-2">
                              Loading bus location...
                            </div>
                          </div>
                        </div>
                      )}

                      {!loading && !locations.length && (
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                          style={{
                            background: "#f8f9fb",
                            zIndex: 600,
                          }}
                        >
                          <div className="text-center text-muted">
                            <i
                              className="ri-map-pin-off-line"
                              style={{
                                fontSize: 52,
                                opacity: 0.3,
                              }}
                            />

                            <div className="fw-semibold mt-2">
                              No GPS location available
                            </div>

                            <small>
                              Location data will appear here once the bus starts
                              reporting.
                            </small>
                          </div>
                        </div>
                      )}

                      <div
                        ref={mapRef}
                        style={{
                          width: "100%",
                          height: "65vh",
                          minHeight: 500,
                          borderRadius: "0 0 8px 8px",
                        }}
                      />

                      {/* Map Status Overlay */}

                      {current && (
                        <div
                          className="position-absolute"
                          style={{
                            left: 15,
                            bottom: 15,
                            zIndex: 700,
                          }}
                        >
                          <div
                            className="bg-white rounded-3 shadow-sm px-3 py-2"
                            style={{
                              minWidth: 190,
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span
                                className={`rounded-circle ${
                                  isLive ? "bg-success" : "bg-secondary"
                                }`}
                                style={{
                                  width: 9,
                                  height: 9,
                                }}
                              />

                              <span className="fw-semibold small">
                                {isLive ? "Bus is Live" : "Location is Stale"}
                              </span>
                            </div>

                            <small className="text-muted">{coordinates}</small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* =====================================================
                      LOCATION HISTORY
                  ===================================================== */}

                  {locations.length > 0 && (
                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-header bg-white border-0 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="fw-bold mb-1">
                              <i className="ri-history-line text-primary me-2" />
                              Location History
                            </h6>

                            <small className="text-muted">
                              {locations.length} location
                              {locations.length !== 1 ? "s" : ""} received
                            </small>
                          </div>

                          <span className="badge bg-label-primary">
                            Tracking: {trackingDuration}
                          </span>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr>
                              <th className="ps-3">#</th>
                              <th>Time</th>
                              <th>Latitude</th>
                              <th>Longitude</th>
                              <th>Driver</th>
                              {/* <th>Trip</th> */}
                              <th>Status</th>
                            </tr>
                          </thead>

                          <tbody>
                            {[...locations].reverse().map((location, index) => {
                              const locationMinutes = minutesSince(
                                location.date_time,
                              );

                              const locationIsLive =
                                locationMinutes !== null &&
                                locationMinutes <= LIVE_THRESHOLD_MIN;

                              return (
                                <tr key={location.id || index}>
                                  <td className="ps-3 text-muted">
                                    {index + 1}
                                  </td>

                                  <td>
                                    <div className="fw-medium">
                                      {formatTime(location.date_time)}
                                    </div>

                                    <small className="text-muted">
                                      {formatDateTime(location.date_time)}
                                    </small>
                                  </td>

                                  <td>
                                    <code>
                                      {parseFloat(location.latitude).toFixed(6)}
                                    </code>
                                  </td>

                                  <td>
                                    <code>
                                      {parseFloat(location.longitude).toFixed(
                                        6,
                                      )}
                                    </code>
                                  </td>

                                  <td>
                                    {location.driver?.name ||
                                      busData?.driver_name ||
                                      "—"}
                                  </td>

                                  {/* <td>
                                    {location.bus_trip ? (
                                      <span className="badge bg-label-info">
                                        Trip #{location.bus_trip.id}
                                      </span>
                                    ) : (
                                      "—"
                                    )}
                                  </td> */}

                                  <td>
                                    <span
                                      className={`badge bg-label-${
                                        locationIsLive ? "success" : "secondary"
                                      }`}
                                    >
                                      {locationIsLive
                                        ? "Live"
                                        : locationMinutes !== null
                                          ? `${locationMinutes}m ago`
                                          : "Unknown"}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          Small local CSS
      ========================================================= */}

      <style>
        {`
    .spin {
      animation: busTrackingSpin 1s linear infinite;
    }

    @keyframes busTrackingSpin {
      from {
        transform: rotate(0deg);
      }

      to {
        transform: rotate(360deg);
      }
    }

    .leaflet-popup-content-wrapper {
      border-radius: 10px;
    }

    .leaflet-popup-content {
      margin: 12px 14px;
    }

    code {
      font-size: 12px;
    }

    /* =====================================================
       LOCATION POINT
    ===================================================== */

    .bus-location-point {
      background: transparent;
      border: none;
    }

    .location-point {
      width: 30px;
      height: 30px;
      border-radius: 50%;

      display: flex;
      align-items: center;
      justify-content: center;

      font-size: 11px;
      font-weight: 700;

      color: #fff;

      border: 3px solid #fff;

      box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.25);
    }

    .location-start {
      background: #198754;
    }

    .location-history {
      background: #6c757d;
    }

    /* =====================================================
       CURRENT LOCATION PULSE
    ===================================================== */

    .current-location-pulse {
      animation: currentLocationPulse 1.8s infinite;
    }

    @keyframes currentLocationPulse {
      0% {
        stroke-opacity: 0.8;
        fill-opacity: 0.18;
      }

      50% {
        stroke-opacity: 0.35;
        fill-opacity: 0.08;
      }

      100% {
        stroke-opacity: 0.8;
        fill-opacity: 0.18;
      }
    }
  `}
      </style>
    </div>
  );
}
